// src/com/tsr/TransPlanTsrEditSupplyFresh.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { TableContainer, Table, TableHead, TableBody, TableCell, TableRow } from '@mui/material';
import { AppWord, AppStyle, CodeGroup } from '../../value/AppConst';
import { codeTreeRepo } from '../../value/AppModel';
import { useReactiveVar } from '@apollo/client';
import ValueMisc from '../../value/ValueMisc';
import VzmSelector from '../../common/VzmSelector';
import VzmIntField from '../../common/VzmIntField';
import { InputTableCell } from '../../common/TopStyle';


const TitleCellBg = '#c5f0e9';

const TemperTitleCell = styled(TableCell)({
    backgroundColor: '#e3fffd',
    color: AppStyle.PrimaryRGB,
    fontWeight: 'bold',
    textAlign: 'center',
});

const TitleCell = styled(TableCell)({
    backgroundColor: TitleCellBg,
    color: AppStyle.PrimaryRGB,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 2
});

const WoodTitleCell = styled(TableCell)({
    backgroundColor: '#e0e3ca',
    color: AppStyle.PrimaryRGB,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 2
});

const SubTitleCell = styled(TableCell)({
    backgroundColor: TitleCellBg,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 2
});

const WORD_OFF = 'off';

const TemperatureValueKey = 'temperature';

const DataDict = {
    tempF: {id: 'tempF', valueKey:TemperatureValueKey}, // 전면(온도)
    fFPalKpp: {id: 'fFPalKpp', check: (value) => value >=0 && value <= 999999}, // Pallet KPP fore
    fFPalAju: {id: 'fFPalAju', check: (value) => value >=0 && value <= 999999}, // Pallet AJU fore
    fFShrFull: {id: 'fFShrFull', check: (value) => value >=0 && value <= 999999}, // Shroud Full fore
    fFShrHalf: {id: 'fFShrHalf', check: (value) => value >=0 && value <= 999999}, // Shroud Half fore
    fFEtcKpp: {id: 'fFEtcKpp', check: (value) => value >=0 && value <= 999999}, // 기타 KPP fore
    fFEtcAju: {id: 'fFEtcAju', check: (value) => value >=0 && value <= 999999}, // 기타 AJU fore
    tempM: {id: 'tempM', valueKey:TemperatureValueKey}, // 중면(온도)
    fMPalKpp: {id: 'fMPalKpp', check: (value) => value >=0 && value <= 999999}, // Pallet KPP mid
    fMPalAju: {id: 'fMPalAju', check: (value) => value >=0 && value <= 999999}, // Pallet AJU mid
    fMShrFull: {id: 'fMShrFull', check: (value) => value >=0 && value <= 999999}, // Shroud Full mid
    fMShrHalf: {id: 'fMShrHalf', check: (value) => value >=0 && value <= 999999}, // Shroud Half mid
    fMEtcKpp: {id: 'fMEtcKpp', check: (value) => value >=0 && value <= 999999}, // 기타 KPP mid
    fMEtcAju: {id: 'fMEtcAju', check: (value) => value >=0 && value <= 999999}, // 기타 AJU mid
    tempR: {id: 'tempR', valueKey:TemperatureValueKey}, // 후면(온도)
    fRPalKpp: {id: 'fRPalKpp', check: (value) => value >=0 && value <= 999999}, // Pallet KPP rear
    fRPalAju: {id: 'fRPalAju', check: (value) => value >=0 && value <= 999999}, // Pallet AJU rear
    fRShrFull: {id: 'fRShrFull', check: (value) => value >=0 && value <= 999999}, // Shroud Full rear
    fRShrHalf: {id: 'fRShrHalf', check: (value) => value >=0 && value <= 999999}, // Shroud Half rear
    fREtcKpp: {id: 'fREtcKpp', check: (value) => value >=0 && value <= 999999}, // 기타 KPP rear
    fREtcAju: {id: 'fREtcAju', check: (value) => value >=0 && value <= 999999}, // 기타 AJU rear
    fNjc: {id: 'fNjc', check: (value) => value >=0 && value <= 999999}, // NJC
    fUcolor: {id: 'fUcolor', check: (value) => value >=0 && value <= 999999}, // 우든칼라
    fOctagon: {id: 'fOctagon', check: (value) => value >=0 && value <= 999999}, // 옥타곤
    fTray: {id: 'fTray', check: (value) => value >=0 && value <= 999999}, // Tray
};

/**
 * 신선 물류집기 편집
 * @param {*} param0
 * @returns 
 */
export default function TransPlanTsrEditSupplyFresh({
    item,
    onChangeValue
}) {
    const [temperatureOptions, setTemperatureOptions] = useState([WORD_OFF]);
    const codeTree = useReactiveVar(codeTreeRepo);

    useEffect(()=>{
        setTemperatureOptions(
            ValueMisc.sortTemperatureCodeOptions(
                ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.TEMPERATURE_CD, TemperatureValueKey))
            );
    }, [codeTree]);

    return(
        <Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TemperTitleCell rowSpan={2} size={AppWord.SMALL}>&nbsp;</TemperTitleCell>
                            <TemperTitleCell rowSpan={2} size={AppWord.SMALL}>온도</TemperTitleCell>
                            <TitleCell colSpan={2} size={AppWord.SMALL}>Pallet</TitleCell>
                            <TitleCell colSpan={2} size={AppWord.SMALL}>Shroud</TitleCell>
                            <TitleCell colSpan={2} size={AppWord.SMALL}>기타</TitleCell>
                        </TableRow>
                        <TableRow>
                            <SubTitleCell size={AppWord.SMALL}>KPP</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>AJU</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>Full</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>Half</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>KPP</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>AJU</SubTitleCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TemperTitleCell>전면</TemperTitleCell>
                            <InputTableCell>
                                <VzmSelector
                                    dict={DataDict.tempF}
                                    value={item.tempF || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fFPalKpp} value={item.fFPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fFPalAju} value={item.fFPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fFShrFull} value={item.fFShrFull}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fFShrHalf} value={item.fFShrHalf}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fFEtcKpp} value={item.fFEtcKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fFEtcAju} value={item.fFEtcAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                        </TableRow>
                        <TableRow>
                            <TemperTitleCell>중면</TemperTitleCell>
                            <InputTableCell>
                                <VzmSelector
                                    dict={DataDict.tempM}
                                    value={item.tempM || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fMPalKpp} value={item.fMPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fMPalAju} value={item.fMPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fMShrFull} value={item.fMShrFull}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fMShrHalf} value={item.fMShrHalf}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fMEtcKpp} value={item.fMEtcKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fMEtcAju} value={item.fMEtcAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                        </TableRow>
                        <TableRow>
                            <TemperTitleCell>후면</TemperTitleCell>
                            <InputTableCell>
                                <VzmSelector
                                    dict={DataDict.tempR}
                                    value={item.tempR || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fRPalKpp} value={item.fRPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fRPalAju} value={item.fRPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fRShrFull} value={item.fRShrFull}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fRShrHalf} value={item.fRShrHalf}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fREtcKpp} value={item.fREtcKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                            <InputTableCell>
                                <VzmIntField
                                    dict={DataDict.fREtcAju} value={item.fREtcAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </InputTableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Box style={{height:20}}></Box>
            <Table style={{maxWidth:600}}>
                <TableHead>
                    <TableRow>
                        <WoodTitleCell>NJC</WoodTitleCell>
                        <WoodTitleCell>우든칼라</WoodTitleCell>
                        <WoodTitleCell>옥타곤</WoodTitleCell>
                        <WoodTitleCell>싱글트레이</WoodTitleCell>
                    </TableRow>
                </TableHead>
                <TableBody style={{backgroundColor: '#f9fff9'}}>
                    <TableRow>
                        <InputTableCell>
                            <VzmIntField
                                dict={DataDict.fNjc} value={item.fNjc}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </InputTableCell>
                        <InputTableCell>
                            <VzmIntField
                                dict={DataDict.fUcolor} value={item.fUcolor}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </InputTableCell>
                        <InputTableCell>
                            <VzmIntField
                                dict={DataDict.fOctagon} value={item.fOctagon}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </InputTableCell>
                        <InputTableCell>
                            <VzmIntField
                                dict={DataDict.fTray} value={item.fTray}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </InputTableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
}

/*

    return(
        <Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TemperTitleCell rowSpan={2} size={AppWord.SMALL}>&nbsp;</TemperTitleCell>
                            <TemperTitleCell rowSpan={2} size={AppWord.SMALL}>온도</TemperTitleCell>
                            <TitleCell colSpan={2} size={AppWord.SMALL}>Pallet</TitleCell>
                            <TitleCell colSpan={2} size={AppWord.SMALL}>Shroud</TitleCell>
                            <TitleCell colSpan={2} size={AppWord.SMALL}>기타</TitleCell>
                        </TableRow>
                        <TableRow>
                            <SubTitleCell size={AppWord.SMALL}>KPP</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>AJU</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>Full</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>Half</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>KPP</SubTitleCell>
                            <SubTitleCell size={AppWord.SMALL}>AJU</SubTitleCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TemperTitleCell>전면</TemperTitleCell>
                            <TableCell>
                                <VzmSelector
                                    dict={DataDict.tempF}
                                    value={item.tempF || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fFPalKpp} value={item.fFPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fFPalAju} value={item.fFPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fFShrFull} value={item.fFShrFull}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fFShrHalf} value={item.fFShrHalf}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fFEtcKpp} value={item.fFEtcKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fFEtcAju} value={item.fFEtcAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TemperTitleCell>중면</TemperTitleCell>
                            <TableCell>
                                <VzmSelector
                                    dict={DataDict.tempM}
                                    value={item.tempM || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fMPalKpp} value={item.fMPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fMPalAju} value={item.fMPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fMShrFull} value={item.fMShrFull}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fMShrHalf} value={item.fMShrHalf}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fMEtcKpp} value={item.fMEtcKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fMEtcAju} value={item.fMEtcAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TemperTitleCell>후면</TemperTitleCell>
                            <TableCell>
                                <VzmSelector
                                    dict={DataDict.tempR}
                                    value={item.tempR || WORD_OFF}
                                    list={temperatureOptions}
                                    onChange={onChangeValue}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fRPalKpp} value={item.fRPalKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fRPalAju} value={item.fRPalAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fRShrFull} value={item.fRShrFull}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fRShrHalf} value={item.fRShrHalf}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fREtcKpp} value={item.fREtcKpp}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                            <TableCell>
                                <VzmIntField
                                    dict={DataDict.fREtcAju} value={item.fREtcAju}
                                    onChange={onChangeValue} fullWidth={true}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Box style={{height:20}}></Box>
            <Table style={{maxWidth:600}}>
                <TableHead>
                    <TableRow>
                        <WoodTitleCell>NJC</WoodTitleCell>
                        <WoodTitleCell>우든칼라</WoodTitleCell>
                        <WoodTitleCell>옥타곤</WoodTitleCell>
                        <WoodTitleCell>싱글트레이</WoodTitleCell>
                    </TableRow>
                </TableHead>
                <TableBody style={{backgroundColor: '#f9fff9'}}>
                    <TableRow>
                        <TableCell>
                            <VzmIntField
                                dict={DataDict.fNjc} value={item.fNjc}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </TableCell>
                        <TableCell>
                            <VzmIntField
                                dict={DataDict.fUcolor} value={item.fUcolor}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </TableCell>
                        <TableCell>
                            <VzmIntField
                                dict={DataDict.fOctagon} value={item.fOctagon}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </TableCell>
                        <TableCell>
                            <VzmIntField
                                dict={DataDict.fTray} value={item.fTray}
                                onChange={onChangeValue} fullWidth={true}
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );

*/
